import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
  IonSpinner,
  IonToast,
  IonCard,
  IonCardContent,
} from "@ionic/react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { addOutline, saveOutline } from "ionicons/icons";
import dayjs from "dayjs";

import { Vendor, VendorProcedure } from "../../../models/vendor";
import * as VendorService from "../../../api/vendor.service";
// import { TaxService } from '../../../api/tax.service';
import * as TaxService from "../../../api/tax.service";
import { Tax } from "../../../models/tax";
import { Procedure } from "../../../models/procedure";
// import { ProcedureService } from '../../../api/procedure.service';
import * as ProcedureService from "../../../api/procedure.service";
import VendorProcedureModal from "./VendorProcedureModal";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const VendorDetail: React.FC = () => {
  // const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const history = useHistory();
  const segment = location.pathname.split("/");
  const id = segment[3];

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // Procedure State
  const [vendorProcedures, setVendorProcedures] = useState<VendorProcedure[]>(
    []
  );
  const [availableProcedures, setAvailableProcedures] = useState<Procedure[]>(
    []
  );
  const [showProcModal, setShowProcModal] = useState(false);

  // Tax State
  const [availableTaxes, setAvailableTaxes] = useState<Tax[]>([]);
  const [selectedTaxId, setSelectedTaxId] = useState<string | null>(null); // Changed to single tax
  const [savingTaxes, setSavingTaxes] = useState(false);

  // Status State
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'activate' | 'disable' | null>(null);

  const [toastMsg, setToastMsg] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  // console.log(id)
  const loadData = async () => {
    setLoading(true);
    try {
      // Parallel fetch
      const [vendorResponse, allProcsResponse, allTaxesResponse] =
        await Promise.all([
          VendorService.getVendor(id),

          ProcedureService.getProcedures(),
          TaxService.getTaxes(),
        ]);

      console.log("Vendor Response:", vendorResponse);
      // Handle vendor data
      const vendorData = vendorResponse.data;
      if (vendorData) {
        setVendor(vendorData);
        // Set the first tax if available, or null

        console.log(vendorData.taxes)
        setSelectedTaxId(
          vendorData.taxes
            ? vendorData.taxes
            : null
        );
        setVendorProcedures(vendorData.procedures || []);
      }

      // Handle all procedures
      const allProcs = allProcsResponse.data?.data || [];
      setAvailableProcedures(allProcs);

      // Handle all taxes
      const allTaxes = allTaxesResponse.data || [];
      setAvailableTaxes(allTaxes);
    } catch (err) {
      console.error(err);
      setToastMsg("Error loading vendor details");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // --- PROCEDURE HANDLERS ---
  const handleSaveProcedures = async (newProcs: VendorProcedure[]) => {
    try {
      await VendorService.saveVendorProcedures({
        vendor_id: id,
        procedures: newProcs,
      });
      // Reload vendor data to get updated procedures
      const response = await VendorService.getVendor(id);
      const vendorData = response.data;
      if (vendorData) {
        setVendorProcedures(vendorData.procedures || []);
      }
      setToastMsg("Procedures assigned successfully");
    } catch (err) {
      setToastMsg("Failed to save procedures");
    }
  };

  const handleDeleteProcedure = async (procedureId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this procedure assignment?"
      )
    ) {
      return;
    }
    try {
      // Filter out the procedure to delete
      const updatedProcedures = vendorProcedures.filter(
        (vp) => vp.procedure_id !== procedureId
      );
      await VendorService.saveVendorProcedures({
        vendor_id: id,
        procedures: updatedProcedures,
      });
      setVendorProcedures(updatedProcedures);
      setToastMsg("Procedure removed successfully");
    } catch (err) {
      setToastMsg("Failed to remove procedure");
    }
  };

  const getProcedureName = (procId: string) => {
    return (
      availableProcedures.find((p) => p.geacloud_procedures_id === procId)
        ?.name || procId
    );
  };

  // --- TAX HANDLERS ---
  const handleTaxChange = (taxId: string) => {
    setSelectedTaxId(taxId);
  };

  const saveTaxes = async () => {
    setSavingTaxes(true);

    // console.log({selectedTaxId,id})
    // return
    try {
      const taxesToSave = selectedTaxId ? selectedTaxId : ''
      console.log("Saving tax:", taxesToSave); // Debug log
      await VendorService.updateVendorTaxes({
        vendor_id: id,
        tax_ids: taxesToSave,
      });
      setToastMsg("Tax configuration saved");
    } catch (err) {
      console.error("Tax save error:", err);
      setToastMsg("Failed to save taxes");
    } finally {
      setSavingTaxes(false);
    }
  };

  // --- STATUS HANDLERS ---
  const handleOpenStatusModal = () => {
    const action = vendor?.status === 1 ? 'disable' : 'activate';
    setPendingAction(action);
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setPendingAction(null);
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingAction) return;

    setShowStatusModal(false);
    setUpdatingStatus(true);
    
    try {
      await VendorService.updateVendorStatus(id, pendingAction);
      // Update local state
      const newStatus = pendingAction === 'activate' ? 1 : 0;
      setVendor(prev => prev ? { ...prev, status: newStatus } : null);
      setToastMsg(`Vendor ${pendingAction}d successfully`);
    } catch (err) {
      console.error("Status update error:", err);
      setToastMsg(`Failed to ${pendingAction} vendor`);
    } finally {
      setUpdatingStatus(false);
      setPendingAction(null);
    }
  };

  if (loading) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          {" "}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            <IonSpinner />{" "}
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  if (!vendor) {
    return (
      <IonPage>
        <IonContent className="ion-padding">Vendor not found</IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage className="ion-padding pageDown">
      <IonHeader>
        <IonToolbar
          style={{
            "--background": "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            "--color": "#ffffff",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              defaultHref="/accounting/vendors"
              style={{ color: "#ffffff" }}
            />
          </IonButtons>
          <IonTitle style={{ fontWeight: 600 }}>{vendor.vendor_name}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ "--background": "#f8fafc" }}>
        <Box sx={{ p: 3 }}>
          {/* Header Card */}
          <Card
            sx={{
              mb: 3,
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {vendor.vendor_name}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={3}
                    flexWrap="wrap"
                    sx={{ color: "text.secondary", mb: 2 }}
                  >
                    <Typography variant="body2">📧 {vendor.email}</Typography>
                    <Typography variant="body2">📞 {vendor.phone}</Typography>
                    {vendor.address && (
                      <Typography variant="body2">
                        📍 {vendor.address}
                      </Typography>
                    )}
                  </Stack>
                  <Chip
                    label={vendor.status === 1 ? "Active" : "Inactive"}
                    color={vendor.status === 1 ? "success" : "default"}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <Button
                    variant="contained"
                    color={vendor.status === 1 ? "error" : "success"}
                    onClick={handleOpenStatusModal}
                    disabled={updatingStatus}
                    sx={{ minWidth: 120 }}
                  >
                    {updatingStatus ? "Updating..." : vendor.status === 1 ? "Disable Vendor" : "Activate Vendor"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Card
            sx={{ borderRadius: 2, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="vendor tabs"
              >
                <Tab label="Procedures" />
                <Tab label="Tax Configuration" />
              </Tabs>
            </Box>

            {/* PROCEDURES TAB */}
            <CustomTabPanel value={tabValue} index={0}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="h6">Assigned Procedures</Typography>
                <IonButton size="small" onClick={() => setShowProcModal(true)}>
                  <IonIcon icon={addOutline} slot="start" />
                  Assign Procedures
                </IonButton>
              </Box>

              <TableContainer
                component={Paper}
                elevation={0}
                variant="outlined"
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell>Procedure Name</TableCell>
                      <TableCell>Agreed Fee</TableCell>
                      <TableCell>Professional Fee</TableCell>
                      <TableCell>Effective Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendorProcedures.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{ py: 4, color: "text.secondary" }}
                        >
                          No procedures assigned yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      vendorProcedures.map((vp) => (
                        <TableRow key={vp.id || vp.procedure_id}>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {getProcedureName(vp.procedure_name)}
                          </TableCell>
                          <TableCell>
                            {vp?.agreed_fee_type === "PERCENTAGE"
                              ? `${vp.agreed_fee}%`
                              : `₦${vp.agreed_fee?.toLocaleString()}`}
                          </TableCell>
                          <TableCell>
                            {vp?.professional_fee_type === "PERCENTAGE"
                              ? `${vp.professional_fee}%`
                              : `₦${vp?.professional_fee.toLocaleString()}`}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              {dayjs(vp.effective_from).format("DD MMM YYYY")} -
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {dayjs(vp.effective_to).format("DD MMM YYYY")}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="center"
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() =>
                                  handleDeleteProcedure(vp.procedure_id)
                                }
                              >
                                Delete
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CustomTabPanel>

            {/* TAXES TAB */}
            <CustomTabPanel value={tabValue} index={1}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="h6">Applicable Taxes</Typography>
                <IonButton
                  size="small"
                  color="success"
                  onClick={saveTaxes}
                  disabled={savingTaxes}
                >
                  <IonIcon icon={saveOutline} slot="start" />
                  Save Configuration
                </IonButton>
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                Select the tax that applies to this vendor's invoices.
              </Typography>

              <Grid container spacing={2}>
                {availableTaxes.map((tax) => (
                  <Grid item xs={12} sm={6} md={4} key={tax.geacloud_taxes_id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        border:
                          selectedTaxId === tax.geacloud_taxes_id
                            ? "2px solid #10b981"
                            : "1px solid #e2e8f0",
                        bgcolor:
                          selectedTaxId === tax.geacloud_taxes_id
                            ? "#f0fdf4"
                            : "transparent",
                        "&:hover": {
                          bgcolor:
                            selectedTaxId === tax.geacloud_taxes_id
                              ? "#f0fdf4"
                              : "#f8fafc",
                        },
                      }}
                      onClick={() => handleTaxChange(tax.geacloud_taxes_id)}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          border: "2px solid",
                          borderColor:
                            selectedTaxId === tax.geacloud_taxes_id
                              ? "#10b981"
                              : "#cbd5e1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        {selectedTaxId === tax.geacloud_taxes_id && (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: "#10b981",
                            }}
                          />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2">{tax.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tax.tax_code} ({parseFloat(tax.rate)}%)
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
                {availableTaxes.length === 0 && (
                  <Grid item xs={12}>
                    <Typography color="text.secondary">
                      No tax definitions found.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CustomTabPanel>
          </Card>
        </Box>
      </IonContent>

      <VendorProcedureModal
        isOpen={showProcModal}
        onClose={() => setShowProcModal(false)}
        vendorId={id}
        onSave={handleSaveProcedures}
      />

      {/* Status Confirmation Modal */}
      <Dialog
        open={showStatusModal}
        onClose={handleCloseStatusModal}
        aria-labelledby="status-dialog-title"
        aria-describedby="status-dialog-description"
      >
        <DialogTitle id="status-dialog-title">
          {pendingAction === 'activate' ? 'Activate Vendor' : 'Disable Vendor'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="status-dialog-description">
            Are you sure you want to {pendingAction} this vendor?
            {pendingAction === 'disable' && (
              <Box sx={{ mt: 1, color: 'warning.main' }}>
                <strong>Note:</strong> Disabling this vendor may affect ongoing operations.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusModal} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmStatusChange} 
            color={pendingAction === 'activate' ? 'success' : 'error'}
            variant="contained"
            autoFocus
          >
            {pendingAction === 'activate' ? 'Activate' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>

      <IonToast
        isOpen={!!toastMsg}
        message={toastMsg || ""}
        duration={2000}
        onDidDismiss={() => setToastMsg(null)}
      />
    </IonPage>
  );
};

export default VendorDetail;
